// Ficha técnica por PRODUCTO con branding NULED.
// Ruta: /producto/:sku/ficha
//
// Regla de negocio: la ficha es por PRODUCTO y despliega sus variantes en una
// matriz compacta (solo las dimensiones que realmente varían). Nunca precios.
// Diseño print-ready (window.print / guardar PDF), mismo lenguaje visual que QuotePdf.

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { thumbUrl } from '../lib/imageUrl';
import { theme } from '../styles/theme';
import {
  productSpecRows,
  commonVariantSpecs,
  configurationColumns,
  type SpecRow,
  type VariantLike,
} from '../lib/specLabels';

type Product = {
  id: string;
  sku: string;
  brand_code: string;
  name: string;
  short_description: string | null;
  description: string | null;
  specs: Record<string, unknown> | null;
  images: unknown;
  warranty: string | null;
  dimensions_cm: Record<string, unknown> | null;
  weight_kg: number | null;
  units_per_box: number | null;
  unit: string | null;
  has_variants: boolean | null;
  category_paths: string[] | null;
  legacy_lutron_line: string | null;
};

const BRAND = theme.brand;
const ACCENT = theme.accent;
const ACCENT_DARK = theme.accentDark;
const TEXT = theme.text;
const MUTED = theme.textMuted;
const BORDER = theme.border;

function splitOpts(v: string): string[] { return String(v || '').split(/[\/,]|·/).map(x => x.trim()).filter(Boolean); }

function cctColor(k: number): string {
  if (!k) return '#c8ccd0';
  if (k <= 2700) return '#FFB765';
  if (k <= 3000) return '#FFCB8E';
  if (k <= 3500) return '#FFDCAF';
  if (k <= 4100) return '#FFEACC';
  if (k <= 5200) return '#FFF7EE';
  return '#EAF1FF';
}
function finishColorHex(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('negro') || n.includes('black')) return '#1a1a1a';
  if (n.includes('blanco') || n.includes('white')) return '#f4f4f4';
  if (n.includes('gris') || n.includes('gray') || n.includes('grey')) return '#9aa0a6';
  if (n.includes('dorado') || n.includes('gold') || n.includes('oro')) return '#c9a34a';
  if (n.includes('bronce') || n.includes('bronze') || n.includes('cobre') || n.includes('copper')) return '#a4703f';
  if (n.includes('plata') || n.includes('silver') || n.includes('alum') || n.includes('crom')) return '#c8ccd0';
  return '#c8ccd0';
}
function BeamCone({ deg, brand }: { deg: number; brand: string }) {
  const w = 110, h = 124, cx = w / 2, topY = 26, len = 76;
  const dx = Math.tan((deg / 2) * Math.PI / 180) * len;
  const floorY = topY + len;
  const gid = `bc${deg}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={`${gid}b`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFE3A6" stopOpacity="0.95" />
          <stop offset="55%" stopColor="#F7C868" stopOpacity="0.52" />
          <stop offset="100%" stopColor="#F2BB50" stopOpacity="0.06" />
        </linearGradient>
        <radialGradient id={`${gid}p`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#F7C868" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#F7C868" stopOpacity="0" />
        </radialGradient>
        <filter id={`${gid}f`} x="-40%" y="-20%" width="180%" height="140%">
          <feGaussianBlur stdDeviation="1.5" />
        </filter>
      </defs>
      <rect x={6} y={14} width={w - 12} height={8} rx={2} fill="#eceef0" />
      <rect x={6} y={14} width={w - 12} height={2.5} fill="#d7dbde" />
      <ellipse cx={cx} cy={floorY} rx={Math.max(dx * 1.15, 8)} ry={Math.max(dx * 0.3, 3)} fill={`url(#${gid}p)`} />
      <polygon points={`${cx},${topY} ${cx - dx},${floorY} ${cx + dx},${floorY}`} fill={`url(#${gid}b)`} filter={`url(#${gid}f)`} />
      <polygon points={`${cx},${topY} ${cx - dx * 0.42},${floorY} ${cx + dx * 0.42},${floorY}`} fill="#FFF1CC" opacity="0.5" />
      <rect x={cx - 10} y={15} width={20} height={9} rx={2} fill={brand} />
      <circle cx={cx} cy={21} r={3} fill="#FFEBB0" />
      <text x={cx} y={h - 5} textAnchor="middle" fontSize="12" fontWeight="700" fill={brand} fontFamily="sans-serif">{deg}°</text>
    </svg>
  );
}
function CodeCol({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>{children}</div>
    </div>
  );
}
function CodeRow({ dot, code, label }: { dot: string; code?: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 15, height: 15, borderRadius: '50%', background: dot, border: `1px solid ${theme.border}`, flexShrink: 0 }} />
      {code ? <span style={{ fontFamily: theme.font.mono, fontSize: 11, fontWeight: 700, color: theme.brand, minWidth: 20 }}>{code}</span> : null}
      <span style={{ fontSize: 11, color: theme.text }}>{label}</span>
    </div>
  );
}

function NuledLogo({ height = 32, color = '#FFFFFF' }: { height?: number; color?: string }) {
  return (
    <svg viewBox="0 0 245.417 58.032" height={height} style={{ display: 'block', color }} aria-label="NULED">
      <path d="M3.289,0,32.517,56.97l.126.247H36.6L7.244,0Z" transform="translate(1.483)" fill="currentColor" />
      <rect width="3.517" height="57.216" transform="translate(0 0)" fill="currentColor" />
      <rect width="3.519" height="57.216" transform="translate(39.336 0)" fill="currentColor" />
      <path d="M77.875,0V35.545c0,12.339-6.013,19.134-16.93,19.134s-16.93-6.795-16.93-19.134V0H40.5V33.995c0,15.726,7.071,24.037,20.447,24.037s20.447-8.311,20.447-24.037V0Z" transform="translate(18.264 -0.001)" fill="currentColor" />
      <path d="M83.163,53.863V0H79.646V57.216h32V53.863Z" transform="translate(35.919 -0.001)" fill="currentColor" />
      <path d="M111.231,53.863V29.143h27.421V25.789H111.231V3.354h28.155V0H107.712V57.216h32.326V53.863Z" transform="translate(48.576 -0.001)" fill="currentColor" />
      <path d="M152,0H138.649V57.216H152c19.343,0,30.893-10.694,30.893-28.607S171.34,0,152,0m27.212,28.609c0,16.521-9.693,25.254-28.029,25.254h-9.013V3.355h9.013c18.336,0,28.029,8.732,28.029,25.254" transform="translate(62.528 -0.001)" fill="currentColor" />
    </svg>
  );
}

// Mismo logo NULED (mismos trazos) como imagen -> se repite bien en el <thead> impreso (el SVG inline no se repinta).
const NULED_LOGO_SVG_WHITE = "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 245.417 58.032'><path d='M3.289,0,32.517,56.97l.126.247H36.6L7.244,0Z' transform='translate(1.483)' fill='%23ffffff'/><rect width='3.517' height='57.216' transform='translate(0 0)' fill='%23ffffff'/><rect width='3.519' height='57.216' transform='translate(39.336 0)' fill='%23ffffff'/><path d='M77.875,0V35.545c0,12.339-6.013,19.134-16.93,19.134s-16.93-6.795-16.93-19.134V0H40.5V33.995c0,15.726,7.071,24.037,20.447,24.037s20.447-8.311,20.447-24.037V0Z' transform='translate(18.264 -0.001)' fill='%23ffffff'/><path d='M83.163,53.863V0H79.646V57.216h32V53.863Z' transform='translate(35.919 -0.001)' fill='%23ffffff'/><path d='M111.231,53.863V29.143h27.421V25.789H111.231V3.354h28.155V0H107.712V57.216h32.326V53.863Z' transform='translate(48.576 -0.001)' fill='%23ffffff'/><path d='M152,0H138.649V57.216H152c19.343,0,30.893-10.694,30.893-28.607S171.34,0,152,0m27.212,28.609c0,16.521-9.693,25.254-28.029,25.254h-9.013V3.355h9.013c18.336,0,28.029,8.732,28.029,25.254' transform='translate(62.528 -0.001)' fill='%23ffffff'/></svg>";
const NULED_LOGO_URI = "data:image/svg+xml," + NULED_LOGO_SVG_WHITE.replace(/ /g, '%20');

type ImageObj = { url?: string; sort_order?: number; is_thumbnail?: boolean; kind?: string };

type ImgObj = { url: string; kind?: string };
function extractImageObjs(raw: unknown): ImgObj[] {
  if (!Array.isArray(raw)) return [];
  const arr = raw as ImageObj[];
  const sorted = [...arr].sort((a, b) => {
    if (a.is_thumbnail && !b.is_thumbnail) return -1;
    if (!a.is_thumbnail && b.is_thumbnail) return 1;
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
  });
  const out: ImgObj[] = [];
  const seen = new Set<string>();
  for (const it of sorted) {
    if (it.url && !seen.has(it.url)) { seen.add(it.url); out.push({ url: it.url, kind: it.kind }); }
  }
  return out;
}

// Reparte las imagenes por tipo. Si estan etiquetadas (image.kind) las respeta;
// si NINGUNA trae kind, cae al reparto por posicion previo (2 primeras = fotos, resto = diagramas).
function classifyImages(objs: ImgObj[]): { fotos: ImgObj[]; diagramas: ImgObj[]; aplicacion: ImgObj[] } {
  const norm = (k?: string) => (k || '').toLowerCase();
  const anyKind = objs.some(o => norm(o.kind));
  if (!anyKind) return { fotos: objs.slice(0, 2), diagramas: objs.slice(2), aplicacion: [] };
  const fotos: ImgObj[] = [], diagramas: ImgObj[] = [], aplicacion: ImgObj[] = [];
  for (const o of objs) {
    const k = norm(o.kind);
    if (k.includes('diagram') || k.includes('dimension') || k.includes('plano') || k.includes('medida')) diagramas.push(o);
    else if (k.includes('aplica') || k.includes('ambien') || k.includes('lifestyle') || k.includes('escena') || k.includes('render')) aplicacion.push(o);
    else fotos.push(o);
  }
  return { fotos, diagramas, aplicacion };
}

function dimsToString(d: Record<string, unknown> | null): string {
  if (!d || typeof d !== 'object') return '';
  const l = d['l']; const w = d['w']; const h = d['h'];
  const parts: string[] = [];
  if (l != null && Number(l) > 0) parts.push(`L ${l}`);
  if (w != null && Number(w) > 0) parts.push(`An ${w}`);
  if (h != null && Number(h) > 0) parts.push(`Al ${h}`);
  return parts.length ? `${parts.join(' × ')} cm` : '';
}

export const FICHA_CSS = `
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
        @media print {
          @page { size: letter; margin: 0; }
          html, body { background: white !important; margin: 0 !important; padding: 0 !important; height: auto !important; overflow: visible !important; }
          /* Imprime SOLO la ficha, sin importar el chrome de la app (evita PDF en blanco) */
          body * { visibility: hidden !important; }
          .pdf-wrap, .pdf-wrap * { visibility: visible !important; }
          .pdf-wrap { max-width: none !important; box-shadow: none !important; margin: 0 !important; }
          .no-print, .no-print * { visibility: hidden !important; display: none !important; }
          .pdf-content { padding: 0.5in 0.5in 0.42in !important; }
          .ficha-thead { display: table-header-group; }
          .spec-table tr, .var-table tr, .avoid-break { page-break-inside: avoid; }
          .sec-title { break-after: avoid; page-break-after: avoid; }
          .keep-together { break-inside: avoid; page-break-inside: avoid; padding-top: 10mm; }
          .pdf-wrap img { break-inside: avoid; }
        }
        body { margin: 0; background: #f5f5f5; font-family: ${theme.font.sans}; }
        .pdf-wrap {
          max-width: 8.5in; margin: 0 auto; background: white; color: ${TEXT};
          font-family: ${theme.font.sans}; font-size: 11px; line-height: 1.45;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        }
        .pdf-content { padding: 0.5in; }
        .ficha-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .ficha-table > tbody > tr > td, .ficha-table > thead > tr > td { padding: 0; }
        .spec-table { width: 100%; border-collapse: collapse; }
        .spec-table td { padding: 8px 10px; border-bottom: 1px solid ${BORDER}; vertical-align: top; }
        @media screen and (max-width: 680px) { .ficha-cols, .ficha-visuals, .ficha-head, .spec-2col { grid-template-columns: 1fr !important; } }
        .var-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        .var-table th { padding: 8px 8px; text-align: left; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; background: ${BRAND}; color: white; }
        .var-table td { padding: 7px 8px; border-bottom: 1px solid ${BORDER}; font-size: 10px; }
`;

import { lutronSpecUrl } from '../lib/lutronSpec';

export function FichaTecnica({ embedSku, embedded = false }: { embedSku?: string; embedded?: boolean } = {}) {
  const params = useParams<{ sku: string }>();
  const sku = embedSku ?? params.sku;
  const [product, setProduct] = useState<Product | null>(null);
  const [variants, setVariants] = useState<VariantLike[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!sku) return;
    (async () => {
      setLoading(true);
      const { data: p } = await supabase.from('products')
        .select('id, sku, brand_code, name, short_description, description, specs, images, warranty, dimensions_cm, weight_kg, units_per_box, unit, has_variants, category_paths, legacy_lutron_line')
        .eq('sku', sku).maybeSingle();
      if (!p) { setNotFound(true); setLoading(false); return; }
      const { data: vs } = await supabase.from('product_variants')
        .select('full_sku, sku_suffix, finish, color_temperature_k, beam_angle_deg, wattage_w, ip_rating, voltage_v, dim_method, attributes')
        .eq('product_id', (p as Product).id)
        .eq('active', true)
        .order('position');
      setProduct(p as Product);
      setVariants((vs as VariantLike[]) ?? []);
      setLoading(false);
    })();
  }, [sku]);

  // Nombre de archivo al guardar/imprimir = modelo público del proveedor.
  useEffect(() => {
    if (product?.sku && !embedded) {
      const prev = document.title;
      const model = String(product.specs?.modelo ?? product.sku);
      document.title = `${model} - Ficha NULED`;
      return () => { document.title = prev; };
    }
  }, [product?.sku, product?.specs, embedded]);

  // Lutron publica su propia ficha oficial: la ruta directa redirige ahí (no generamos ficha Lutron).
  useEffect(() => {
    if (product?.brand_code === 'lutron' && !embedded) {
      const url = lutronSpecUrl(product.sku);
      if (url) window.location.replace(url);
    }
  }, [product?.brand_code, embedded]);

  if (loading) return <div style={{ padding: 40, fontFamily: theme.font.sans }}>Cargando ficha…</div>;
  if (notFound || !product) return <div style={{ padding: 40, fontFamily: theme.font.sans }}>Producto no encontrado</div>;

  const publicModel = String(product.specs?.modelo ?? product.sku);

  // Producto Lutron → ficha oficial Lutron (no la generada). En ruta directa ya redirige (efecto arriba);
  // aquí se muestra tarjeta con enlace (sirve para el bundle embebido y como fallback si el redirect no corre).
  if (product.brand_code === 'lutron') {
    const oficial = lutronSpecUrl(product.sku) || 'https://support.lutron.com/us/en/';
    return (
      <div style={{ padding: embedded ? 24 : 48, fontFamily: theme.font.sans, textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
        <div style={{ fontFamily: theme.font.head, fontSize: 18, fontWeight: 700, color: theme.brand }}>{product.sku}</div>
        <div style={{ fontSize: 14, color: theme.textMuted, marginTop: 4 }}>{product.name}</div>
        <div style={{ fontSize: 13, color: theme.textMuted, margin: '16px 0' }}>Lutron mantiene su propia ficha técnica oficial, siempre actualizada.</div>
        <a href={oficial} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', background: theme.accent, color: theme.brand, textDecoration: 'none', padding: '12px 22px', fontWeight: 700, fontSize: 13, letterSpacing: 0.4, textTransform: 'uppercase', fontFamily: theme.font.sans }}>Ver ficha oficial Lutron ↗</a>
      </div>
    );
  }

  const imgObjs = extractImageObjs(product.images);
  const { fotos, diagramas, aplicacion } = classifyImages(imgObjs);

  // Specs de producto = specs jsonb + specs comunes derivadas de variantes.
  const baseRows = productSpecRows(product.specs);
  const commonRows = commonVariantSpecs(variants);
  const seenKeys = new Set(baseRows.map(r => r.key));
  const specRows: SpecRow[] = [...baseRows];
  for (const r of commonRows) {
    if (!seenKeys.has(r.key)) { specRows.push(r); seenKeys.add(r.key); }
  }

  const dimStr = dimsToString(product.dimensions_cm);
  if (dimStr && !seenKeys.has('dimensiones')) {
    specRows.push({ key: 'dimensiones', label: 'Dimensiones', value: dimStr });
  }

  const varCols = configurationColumns(variants);
  const showMatrix = variants.length > 0 && varCols.length > 0;


  const specGet = (pred: (k: string) => boolean): string => {
    const sp = product.specs ?? {};
    for (const k of Object.keys(sp)) { if (pred(k.toLowerCase())) { const val = sp[k]; if (val != null && String(val).trim() !== '') return String(val); } }
    return '';
  };
  // Los rangos ajustables (p. ej. 10°–55°) no son un ángulo discreto y no deben
  // convertirse en "1055°" dentro del diagrama ni de la formación de código.
  const anglesArr = splitOpts(specGet(k => k.includes('angul')))
    .filter(a => !/[–—-]/.test(a))
    .map(a => parseInt(a.replace(/[^\d]/g, ''), 10))
    .filter(n => !isNaN(n));
  const cctOpts = splitOpts(specGet(k => k === 'cct' || (k.includes('temperatura') && k.includes('color'))));
  const finishOpts = splitOpts(specGet(k => k.includes('acabado') || k.includes('finish')));
  const dimOpts = splitOpts(specGet(k => k.includes('dimeo') || k.includes('atenu')));
  const hasCodigo = (finishOpts.length + cctOpts.length + anglesArr.length + dimOpts.length) > 1;

  return (
    <>
      {!embedded && <style>{FICHA_CSS}</style>}

      {/* Barra de acciones (no imprime) */}
      {!embedded && <div className="no-print" style={{
        background: BRAND, color: 'white', padding: '14px 24px',
        display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between',
        fontFamily: theme.font.sans,
      }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <NuledLogo height={20} color="white" />
          <span style={{ fontSize: 11, opacity: 0.7 }}>Ficha técnica · {publicModel}</span>
        </div>
        <button onClick={() => window.print()} style={{
          background: ACCENT, color: BRAND, border: 'none', padding: '8px 18px',
          fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
          cursor: 'pointer', fontFamily: theme.font.sans,
        }}>📥 Descargar / Imprimir</button>
      </div>}

      <table className="pdf-wrap ficha-table">
        {/* HEADER en thead -> el navegador lo REPITE en cada pagina impresa; el contenido siempre va debajo (no se encima) */}
        <thead className="ficha-thead"><tr><td style={{ padding: 0 }}>
        {/* Franja delgada que se repite en cada pagina: logo (texto, renderiza al repetir) izq + modelo der */}
        <div style={{ background: BRAND, color: 'white', padding: '18px 0.5in', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <img src={NULED_LOGO_URI} alt="NULED" style={{ height: 26, display: 'block' }} />
            <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.6)', letterSpacing: 1.4, textTransform: 'uppercase', fontWeight: 600 }}>Iluminación LED · Mayorista Lutron</span>
          </div>
          <div style={{ textAlign: 'right', lineHeight: 1.15 }}>
            <span style={{ fontSize: 8, fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: 2, marginRight: 10 }}>Ficha técnica</span>
            <span style={{ fontFamily: theme.font.mono, fontSize: 13, fontWeight: 700, color: 'white' }}>{publicModel}</span>
          </div>
        </div>
        <div style={{ height: 3, background: ACCENT }} />
        </td></tr></thead>
        <tbody><tr><td style={{ padding: 0 }}>
        <div className="pdf-content">
          {/* TÍTULO + DESCRIPCIÓN + FOTOS PRINCIPALES (lado a lado) */}
          <div className="ficha-head" style={{ display: 'grid', gridTemplateColumns: fotos.length > 0 ? '1fr 340px' : '1fr', gap: 28, alignItems: 'start', marginBottom: 24 }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 300, color: BRAND, letterSpacing: -0.5, lineHeight: 1.15 }}>{product.name}</h1>
              {(product.description || product.short_description) && (
                <div style={{ fontSize: 12, color: MUTED, marginTop: 10, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                  {stripHtml(product.description || product.short_description || '')}
                </div>
              )}
              {product.category_paths && product.category_paths.length > 0 && (
                <div style={{ fontSize: 9, color: MUTED, marginTop: 10, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  {product.category_paths[product.category_paths.length - 1]}
                </div>
              )}
            </div>
            {fotos.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: fotos.length > 1 ? 'repeat(2, 1fr)' : '1fr', gap: 10 }}>
                {fotos.map((o, i) => (
                  <img key={i} src={thumbUrl(o.url, 800)} alt={i === 0 ? product.name : ''} loading="eager" style={{ width: '100%', height: 168, objectFit: 'contain', background: 'transparent' }} />
                ))}
              </div>
            )}
          </div>

          {/* ESPECIFICACIONES (a lo ancho, 2 columnas) */}
          {specRows.length > 0 ? (
            <div style={{ marginBottom: 24 }}>
              <SectionTitle>Especificaciones</SectionTitle>
              <div className="spec-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 40px', alignItems: 'start' }}>
                {[specRows.slice(0, Math.ceil(specRows.length / 2)), specRows.slice(Math.ceil(specRows.length / 2))].map((half, hi) => (
                  <table key={hi} className="spec-table">
                    <tbody>
                      {half.map(r => (
                        <tr key={r.key}>
                          <td style={{ width: '46%', fontSize: 10, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{r.icon ? `${r.icon} ` : ''}{r.label}</td>
                          <td style={{ fontSize: 12, fontWeight: 600, color: BRAND }}>{r.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}
              </div>
            </div>
          ) : (
            <div className="avoid-break" style={{ padding: 16, background: theme.accentSoft, border: `1px solid ${ACCENT_DARK}`, fontSize: 11, color: BRAND, marginBottom: 24 }}>
              Este producto todavía no tiene especificaciones capturadas. Complétala desde el módulo de productos para enriquecer su ficha.
            </div>
          )}

          {/* DIMENSIONES Y DIAGRAMAS */}
          {diagramas.length > 0 && (
            <div className="keep-together" style={{ marginBottom: 24 }}>
              <SectionTitle>Dimensiones y diagramas</SectionTitle>
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', justifyContent: 'center' }}>
                {diagramas.map((o, i) => (
                  <img key={i} src={thumbUrl(o.url, 1000)} alt="Diagrama de dimensiones" loading="eager" style={{ maxWidth: 320, maxHeight: 230, width: 'auto', height: 'auto', objectFit: 'contain' }} />
                ))}
              </div>
            </div>
          )}

          {/* EN APLICACIÓN */}
          {aplicacion.length > 0 && (
            <div className="keep-together" style={{ marginBottom: 24 }}>
              <SectionTitle>En aplicación</SectionTitle>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
                {aplicacion.map((o, i) => (
                  <img key={i} src={thumbUrl(o.url, 1200)} alt="Aplicación" loading="eager" style={{ maxWidth: 460, maxHeight: 300, width: 'auto', height: 'auto', objectFit: 'contain' }} />
                ))}
              </div>
            </div>
          )}

          {/* ÁNGULOS DE APERTURA (conos) */}
          {anglesArr.length > 0 && (
            <div className="keep-together" style={{ marginBottom: 24 }}>
              <SectionTitle>Ángulos de apertura</SectionTitle>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {anglesArr.map(a => <BeamCone key={a} deg={a} brand={BRAND} />)}
              </div>
            </div>
          )}

          {/* FORMACIÓN DE CÓDIGO */}
          {hasCodigo && (
            <div className="keep-together" style={{ marginBottom: 24 }}>
              <SectionTitle>Formación de código</SectionTitle>
              <div style={{ fontFamily: theme.font.mono, fontSize: 14, fontWeight: 700, color: BRAND, marginBottom: 12 }}>{publicModel}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 18 }}>
                {finishOpts.length > 0 && (
                  <CodeCol title="Acabado">
                    {finishOpts.map(f => <CodeRow key={f} dot={finishColorHex(f)} code={(f[0] || '').toUpperCase()} label={f} />)}
                  </CodeCol>
                )}
                {cctOpts.length > 0 && (
                  <CodeCol title="Temperatura de color">
                    {cctOpts.map(c => { const k = parseInt(c.replace(/[^\d]/g, ''), 10); return <CodeRow key={c} dot={cctColor(k)} code={String(k).slice(0, 2)} label={c} />; })}
                  </CodeCol>
                )}
                {anglesArr.length > 0 && (
                  <CodeCol title="Ángulo de apertura">
                    {anglesArr.map(a => <CodeRow key={a} dot={ACCENT} code={String(a)} label={`${a}°`} />)}
                  </CodeCol>
                )}
                {dimOpts.length > 0 && (
                  <CodeCol title="Atenuación">
                    {dimOpts.map(d => <CodeRow key={d} dot={BRAND} label={d} />)}
                  </CodeCol>
                )}
              </div>
            </div>
          )}

          {/* MATRIZ DE VARIANTES */}
          {showMatrix && (
            <div style={{ marginBottom: 24 }}>
              <SectionTitle>Configuraciones disponibles ({variants.length})</SectionTitle>
              <table className="var-table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    {varCols.map(c => <th key={c.key}>{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {variants.map(v => (
                    <tr key={v.full_sku}>
                      <td style={{ fontFamily: theme.font.mono, fontWeight: 700 }}>{v.full_sku}</td>
                      {varCols.map(c => <td key={c.key}>{c.get(v) || '—'}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}


          {/* LOGÍSTICA + GARANTÍA */}
          {(product.weight_kg || product.units_per_box || product.warranty) && (
            <div className="avoid-break" style={{
              marginBottom: 8, padding: 16, background: theme.bgSoft,
              border: `1px solid ${BORDER}`, borderLeft: `4px solid ${ACCENT}`,
            }}>
              {(product.weight_kg || product.units_per_box) && (
                <div style={{ fontSize: 10, color: TEXT, marginBottom: product.warranty ? 10 : 0, lineHeight: 1.7 }}>
                  {product.units_per_box ? <span><strong>Piezas por caja:</strong> {product.units_per_box}&nbsp;&nbsp;·&nbsp;&nbsp;</span> : null}
                  {product.weight_kg ? <span><strong>Peso:</strong> {product.weight_kg} kg</span> : null}
                </div>
              )}
              {product.warranty && (
                <div style={{ fontSize: 10, color: TEXT, lineHeight: 1.6 }}>
                  <div style={{ fontSize: 9, color: BRAND, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Garantía</div>
                  {product.warranty}
                </div>
              )}
            </div>
          )}

          {/* FOOTER */}
          <div style={{ marginTop: 28, paddingTop: 14, borderTop: `1px solid ${BORDER}`, textAlign: 'center' }}>
            <NuledLogo height={13} color={BRAND} />
            <div style={{ fontSize: 8, color: MUTED, marginTop: 8, letterSpacing: 0.4 }}>
              NULED · Iluminación LED · nuled.mx · ventas@nuled.mx · Especificaciones sujetas a cambio sin previo aviso.
            </div>
          </div>
        </div>
        </td></tr></tbody>
      </table>
    </>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="sec-title" style={{
      fontSize: 11, color: BRAND, fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: 1.2, marginBottom: 8, paddingBottom: 6, borderBottom: `2px solid ${ACCENT}`,
    }}>
      {children}
    </div>
  );
}

function stripHtml(s: string): string {
  const ent: Record<string, string> = { nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", deg: '\u00b0', ordm: '\u00ba', micro: '\u00b5', times: '\u00d7', aacute: '\u00e1', eacute: '\u00e9', iacute: '\u00ed', oacute: '\u00f3', uacute: '\u00fa', ntilde: '\u00f1', uuml: '\u00fc', Aacute: '\u00c1', Eacute: '\u00c9', Iacute: '\u00cd', Oacute: '\u00d3', Uacute: '\u00da', Ntilde: '\u00d1', Uuml: '\u00dc' };
  return s
    .replace(/<[^>]*>/g, ' ')
    .replace(/&#(\d+);/g, (_m, n) => String.fromCharCode(parseInt(n, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_m, n) => String.fromCharCode(parseInt(n, 16)))
    .replace(/&([a-zA-Z]+);/g, (m, name) => ent[name] ?? m)
    .replace(/\s{2,}/g, ' ')
    .trim();
}
